import { FormControl, SxProps, TextField, Theme } from "@mui/material";
import { useField } from "formik";
import { FC } from "react";
import MaskedInput from "react-text-mask";

interface FieldProps {
  name: string;
  label?: string;
  type?: string;
  disabled?: boolean;
  mask?: (string | RegExp)[] | ((value: string) => (string | RegExp)[]);
  disablePaste?: boolean;
  enableNumericHandler?: boolean;
  dataT1?: string;
  required?: boolean;
  isDataT1NotChanged?: boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
}

const FieldMUI: FC<FieldProps> = ({
  name,
  label,
  type = "text",
  placeholder,
  disabled = false,
  mask,
  disablePaste,
  enableNumericHandler,
  dataT1 = "field",
  required = false,
  isDataT1NotChanged = false,
  sx,
}) => {
  const [field, meta, helpers] = useField(name);
  const isError = meta.touched && Boolean(meta.error);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const val = e.target.value;
    if (enableNumericHandler && type === "number") {
      if (!/^[-]?\d*$/.test(val)) return;
    }
    helpers.setValue(val);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (disablePaste) e.preventDefault();
  };

  if (mask) {
    return (
      <FormControl sx={sx} fullWidth error={isError} margin="normal">
        <MaskedInput
          mask={mask}
          value={field.value}
          onChange={(e) => helpers.setValue(e.target.value)}
          onBlur={() => helpers.setTouched(true)}
          render={(ref, props) => (
            <TextField
              {...props}
              required={required}
              inputRef={ref}
              label={label}
              placeholder={placeholder ?? "- - - - - - - - - - -"}
              fullWidth
              data-t1={isDataT1NotChanged ? dataT1 : `${dataT1}_${name}`}
              disabled={disabled}
              onPaste={handlePaste}
              error={isError}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                ...sx,
                "& .MuiFormHelperText-root": {
                  ml: 0,
                },
              }}
              helperText={isError ? meta.error : ""}
            />
          )}
        />
      </FormControl>
    );
  }

  if (placeholder) {
    return (
      <TextField
        {...field}
        required={required}
        type={type}
        label={label}
        fullWidth
        sx={{
          ...sx,
          "& .MuiFormHelperText-root": {
            ml: 0,
          },
        }}
        variant="outlined"
        disabled={disabled}
        onChange={handleChange}
        placeholder={placeholder}
        onBlur={() => helpers.setTouched(true)}
        onPaste={handlePaste}
        error={isError}
        data-t1={isDataT1NotChanged ? dataT1 : `${dataT1}_${name}`}
        helperText={isError ? meta.error : ""}
        margin="normal"
        slotProps={{ inputLabel: { shrink: true } }}
      />
    );
  }

  return (
    <TextField
      {...field}
      required={required}
      type={type}
      label={label}
      fullWidth
      sx={{
        ...sx,
        "& .MuiFormHelperText-root": {
          ml: 0,
        },
      }}
      variant="outlined"
      disabled={disabled}
      onChange={handleChange}
      onBlur={() => helpers.setTouched(true)}
      onPaste={handlePaste}
      error={isError}
      data-t1={isDataT1NotChanged ? dataT1 : `${dataT1}_${name}`}
      helperText={isError ? meta.error : ""}
      margin="normal"
    />
  );
};

export default FieldMUI;
